import React, { FC, useEffect, useState, Fragment } from "react"
import clsx from "clsx"
import { Dialog, Transition } from "@headlessui/react"
import {
  EmailShareButton,
  EmailIcon,
  FacebookShareButton,
  FacebookIcon,
  LinkedinShareButton,
  LinkedinIcon,
  TelegramShareButton,
  TelegramIcon,
  TwitterShareButton,
  TwitterIcon,
  WhatsappShareButton,
  WhatsappIcon,
} from "react-share"
import { IoCopy } from "react-icons/io5"
import { FiShare2 } from "react-icons/fi"
import { FaTimes } from "react-icons/fa"

import MiscServices from "@services/misc.services"
import { Spinner } from "@components/misc"

export interface ModalShareProps {
  slug: string
  show: boolean
  onClose: () => void
}

interface ShareSocialMedia {
  name: string
  ShareButton: any
  ShareIcon: any
}

const shareSocialMedias: ShareSocialMedia[] = [
  {
    name: "Email",
    ShareButton: EmailShareButton,
    ShareIcon: EmailIcon,
  },
  {
    name: "Facebook",
    ShareButton: FacebookShareButton,
    ShareIcon: FacebookIcon,
  },
  {
    name: "Twitter",
    ShareButton: TwitterShareButton,
    ShareIcon: TwitterIcon,
  },
  {
    name: "Linkedin",
    ShareButton: LinkedinShareButton,
    ShareIcon: LinkedinIcon,
  },
  { name: "Whatsapp", ShareButton: WhatsappShareButton, ShareIcon: WhatsappIcon },
  {
    name: "Telegram",
    ShareButton: TelegramShareButton,
    ShareIcon: TelegramIcon,
  },
]

export const ModalShare: FC<ModalShareProps> = ({ slug, show, onClose }) => {
  const [isFetchingLink, setIsFetchingLink] = useState(false)
  const [shareLink, setShareLink] = useState("")
  const [linkCopied, setLinkCopied] = useState(false)

  const misc = new MiscServices()

  const handleCopy = async () => {
    await navigator.clipboard.writeText(shareLink)
    setLinkCopied(true)
    setTimeout(() => setLinkCopied(false), 3000)
  }

  useEffect(() => {
    const getLink = async () => {
      try {
        setIsFetchingLink(true)
        const res = await misc.getShareLink(slug)

        if (res.isSuccess) {
          const data = res.getValue() as string
          setShareLink(data)
          setIsFetchingLink(false)
        } else {
          setShareLink(`https://harisenin.link/${slug}`)
          setIsFetchingLink(false)
        }
      } catch (e) {
        setShareLink(`https://harisenin.link/${slug}`)
        setIsFetchingLink(false)
      }
    }

    if (show) {
      getLink()
    }

    return () => setShareLink("")
  }, [show])

  return (
    <Transition appear show={show} as={Fragment}>
      <Dialog as="div" className="fixed inset-0 z-[1001] overflow-y-auto" onClose={onClose}>
        <div className="h-screen flex flex-col sm:justify-center justify-end items-center">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-backdrop" />
          </Transition.Child>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom={clsx("sm:translate-y-0 sm:opacity-0", "translate-y-full")}
            enterTo={clsx("sm:opacity-100", "translate-y-0 ")}
            leave={clsx("ease-in sm:duration-200", "duration-500")}
            leaveFrom={clsx("sm:opacity-100", "translate-y-0 ")}
            leaveTo={clsx("sm:translate-y-0 sm:opacity-0", "translate-y-full ")}
          >
            <div className="w-full z-[1002]  max-w-lg py-4 px-5 overflow-hidden text-left bg-white shadow-xl sm:rounded-xl rounded-t-xl">
              <div className="flex justify-between items-center">
                <h4 className={clsx("font-medium sm:text-lg", "text-md")}>Share Pekerjaan</h4>
                <button onClick={onClose}>
                  <FaTimes className="w-5 h-5" />
                </button>
              </div>

              {isFetchingLink ? (
                <div className="sm:h-52 h-72 flex flex-col items-center justify-center">
                  <Spinner />
                </div>
              ) : (
                <>
                  <div className={clsx("grid sm:grid-cols-6 gap-y-6 mt-8", "grid-cols-3")}>
                    {shareSocialMedias.map(
                      ({ ShareButton, ShareIcon, name }: ShareSocialMedia, index: number) => (
                        <div key={index} className="flex flex-col items-center gap-2">
                          <ShareButton url={shareLink}>
                            <ShareIcon size={48} className="rounded-md" />
                          </ShareButton>
                          <div className="text-sm">{name}</div>
                        </div>
                      )
                    )}
                  </div>
                  <div className="flex flex-col mt-6">
                    <div className="flex justify-between border-[1px] py-3 px-5 bg-grey-f7 border-grey-ec rounded-[4px] mb-1">
                      <div className="sm:text-base text-sm">{shareLink}</div>
                      <button className="text-green" onClick={handleCopy}>
                        <IoCopy className="h-4 w-4" />
                      </button>
                    </div>
                    {linkCopied ? (
                      <small className="text-green h-4">Link berhasil disalin</small>
                    ) : (
                      <div className="h-4"></div>
                    )}
                  </div>
                </>
              )}
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  )
}
